import { Button, Card } from '../../../components';
import { BookingModel } from '../../../data/entities/booking/BookingModel';
import { sharedProperty } from '../../../hooks';
import { BookingScheduleModal, ConfirmBookingModal } from '../components';
import BookingEventItem from './BookingEventItem';
import { BookingEventModel } from '../../../data/entities/booking/BookingEventModel';
import { useBookingEvents } from '../hooks/useBookingEvents';
import { router } from '../../../app/routes';
import BookingService from '../../../services/booking/BookingService';
import { BookingInvoice } from '../../../models/booking/BookingInvoice';
import { formatDateToLongUsWeekday, saveCustomerBookingInfo, trackBeginCheckout, trackCheckoutStep } from '../../../utils';
import { useState } from 'react';

interface BookingSummaryProps {
  booking: sharedProperty<BookingModel>;
  locationId: number;
  locationCode: string;
  compact?: boolean;
}

export const BookingSummary = ({ booking, locationId, locationCode, compact = false } : BookingSummaryProps) => {
  const bookingService = new BookingService();
  const [scheduleConflictError, setScheduleConflictError] = useState<string | undefined>(undefined);

  const {
    showScheduleModalState,
    showConfirmModalState,
    handleDelete,
  } = useBookingEvents(booking);

  if (!booking.value) return null;

  const handleNext = () => {
    trackBeginCheckout(booking.value.bookingEvents);
    showScheduleModalState.setter(true);
  };

  const modals = (
    <>
      <BookingScheduleModal
        showState={showScheduleModalState}
        booking={booking}
        onSubmit={() => {
          showScheduleModalState.setter(false);
          showConfirmModalState.setter(true);
          setScheduleConflictError(undefined);
        }}
        locationId={locationId}
        conflictError={scheduleConflictError}
      />
      <ConfirmBookingModal
        showState={showConfirmModalState}
        booking={booking}
        onSubmit={async (bookingItem: BookingModel) => {
          const bookingInvoice: BookingInvoice = await bookingService.initiateBooking(bookingItem) ?? {} as BookingInvoice;
          
          if(bookingInvoice){
            saveCustomerBookingInfo({
              firstName: bookingItem.customer?.firstName,
              lastName: bookingItem.customer?.lastName,
              email: bookingItem.customer?.email,
              phone: bookingItem.customer?.phone,
              sendConfirmationToPhone: bookingItem.sendConfirmationToPhone,
            });

            localStorage.setItem("activeBookingId", bookingInvoice.booking.id.toString());
            localStorage.setItem("activeBookingInvoiceId", bookingInvoice.invoice.id.toString());
            showConfirmModalState.setter(false);
            const anyProviderFlags = bookingItem.bookingEvents.map(e => e.isAnyProvider ?? false);
            localStorage.setItem("activeBookingAnyProviderFlags", JSON.stringify(anyProviderFlags));
            trackCheckoutStep("booking_initiated", bookingInvoice.booking, bookingInvoice.invoice);
            showConfirmModalState.setter(false);
            router.navigate(`/invoice/${locationCode}`);
          }
    
        }}
        onConflict={() => {
          booking.setter({
            ...booking.value,
            bookingEvents: booking.value.bookingEvents.map(e => ({
              ...e,
              startTime: null,
              endTime: null,
            }))
          });
          showConfirmModalState.setter(false);
          setScheduleConflictError('The selected time is no longer available. Please select a new time.');
          showScheduleModalState.setter(true);
        }}
        onBack={() => {
          showConfirmModalState.setter(false);
          showScheduleModalState.setter(true);
        }}
      />
    </>
  );

  if (compact) {
    return (
      <div className="d-flex flex-column" style={{ height: '100%' }}>
        <div
          className="flex-grow-1"
          style={{
            overflowY: 'scroll',
            minHeight: 0,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="px-3">
            {booking.value.date && (
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <i className="fa fa-calendar fa-lg text-primary me-3"></i>
                  <div>
                    <div className="text-muted small mb-1">Appointment Date</div>
                    <div className="fw-bold">{formatDateToLongUsWeekday(booking.value.date)}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-3">
              {booking.value.bookingEvents.map((event: BookingEventModel) => (
                <BookingEventItem key={event.id} event={event} locationId={locationId} onDelete={handleDelete}/>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-3 px-3 pb-3 border-top" style={{ flexShrink: 0 }}>
          <Button variant="primary" className="w-100" onClick={handleNext}>
            Next
          </Button>
        </div>

        {modals}
      </div>
    );
  }

  return (
    <Card>
      <div className="mb-4">
        <h3 className="mb-0">Your Booking</h3>
      </div>

      {booking.value.date && (
        <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="d-flex align-items-center">
            <i className="fa fa-calendar fa-lg text-primary me-3"></i>
            <div>
              <div className="text-muted small mb-1">Appointment Date</div>
              <div className="fw-bold">{formatDateToLongUsWeekday(booking.value.date)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-3">
        {booking.value.bookingEvents.map((event: BookingEventModel) => (
          <BookingEventItem 
            key={event.id} 
            event={event} 
            locationId={locationId} 
            onDelete={handleDelete}/>
        ))}
      </div>
      <Button variant="primary" onClick={handleNext}>
        Next
      </Button>

      {modals}
    </Card>
  );
};

export default BookingSummary;